import React, { useEffect, useState } from "react";
import { PiGift } from "react-icons/pi";
import { useCustomer } from "../context/CustomerContext";
import {
  buildLoyaltyExplanation,
  formatKopecks,
  hryvniaInputToKopecks,
  kopecksToHryvniaInput,
  normalizeLoyaltySettings,
} from "../utils/loyalty";
import AuthCustomerModal from "./AuthCustomerModal";
import RegisterCustomerModal from "./RegisterCustomerModal";
import styles from "./CheckoutLoyaltyPanel.module.css";

const CheckoutLoyaltyPanel = ({
  settings,
  quote,
  requestedBonusKopecks = 0,
  loading = false,
  error = "",
  onRequestedBonusKopecksChange,
  onUseMaximum,
  onCancel,
  onRetry,
}) => {
  const { customer } = useCustomer();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const normalizedSettings = normalizeLoyaltySettings(settings);
  const programEnabled = normalizedSettings.earningEnabled;
  const canRedeem = normalizedSettings.redemptionEnabled;
  const registered = Boolean(customer && quote?.registered !== false);
  const availableBonusKopecks = quote?.availableBonusKopecks ?? 0;
  const maxRedeemableBonusKopecks = canRedeem ? quote?.maxRedeemableBonusKopecks ?? 0 : 0;
  const appliedBonusKopecks = quote?.appliedBonusKopecks ?? 0;
  const [amountInput, setAmountInput] = useState(kopecksToHryvniaInput(requestedBonusKopecks));

  useEffect(() => {
    setAmountInput(kopecksToHryvniaInput(requestedBonusKopecks));
  }, [requestedBonusKopecks]);

  const rememberCheckoutPath = () => {
    localStorage.setItem("lastVisitedPath", "/cart");
  };

  const openLogin = () => {
    rememberCheckoutPath();
    setShowRegister(false);
    setShowLogin(true);
  };

  const openRegister = () => {
    rememberCheckoutPath();
    setShowLogin(false);
    setShowRegister(true);
  };

  const handleBonusAmountChange = (event) => {
    if (!canRedeem) return;
    const rawValue = event.target.value;
    const kopecks = hryvniaInputToKopecks(rawValue);
    if (kopecks === null) return;
    setAmountInput(rawValue);
    onRequestedBonusKopecksChange?.(kopecks);
  };

  if (!settings || !normalizedSettings.visible) return null;

  return (
    <section className={styles.panel} aria-labelledby="loyalty-checkout-title">
      <div className={styles.headingRow}>
        <span className={styles.icon} aria-hidden="true">
          <PiGift />
        </span>
        <div>
          <h3 id="loyalty-checkout-title">Бонусна програма</h3>
          {settings ? (
            <p className={styles.explanation}>
              {buildLoyaltyExplanation(settings)}
            </p>
          ) : (
            <p className={styles.explanation}>Завантажуємо актуальні правила…</p>
          )}
        </div>
      </div>

      {!programEnabled && settings ? (
        <p className={styles.notice}>Нарахування та списання бонусів зараз вимкнено.</p>
      ) : !customer ? (
        <div className={styles.guestBox}>
          <p>
            Програма доступна лише зареєстрованим користувачам. Увійдіть або
            зареєструйтеся, щоб накопичувати бонуси.
          </p>
          <div className={styles.guestActions}>
            <button type="button" onClick={openLogin}>
              Увійти
            </button>
            <button type="button" className={styles.secondaryButton} onClick={openRegister}>
              Зареєструватися
            </button>
          </div>
        </div>
      ) : registered ? (
        <div className={styles.redeemBox}>
          <p className={styles.balance}>
            Ваш бонусний баланс: <strong>{formatKopecks(availableBonusKopecks)}</strong>
          </p>
          <p className={styles.limit}>
            Ви можете використати до{" "}
            <strong>{formatKopecks(maxRedeemableBonusKopecks)}</strong> бонусів у цьому замовленні.
          </p>

          <label className={styles.inputLabel} htmlFor="loyalty-bonus-amount-input">
            Сума бонусів, грн
            <input
              id="loyalty-bonus-amount-input"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]{0,2}"
              value={amountInput}
              onChange={handleBonusAmountChange}
              disabled={!canRedeem || loading || maxRedeemableBonusKopecks <= 0}
            />
          </label>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onUseMaximum}
              disabled={!canRedeem || loading || maxRedeemableBonusKopecks <= 0}
            >
              Використати максимальний розмір бонуса
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onCancel}
              disabled={!canRedeem || loading || (requestedBonusKopecks <= 0 && appliedBonusKopecks <= 0)}
            >
              Не використовувати
            </button>
          </div>

          {appliedBonusKopecks > 0 && (
            <p className={styles.applied} aria-live="polite">
              Бонусна знижка: −{formatKopecks(appliedBonusKopecks)}
            </p>
          )}
        </div>
      ) : (
        <p className={styles.notice}>
          Не вдалося підтвердити участь у програмі. Оновіть розрахунок або увійдіть
          повторно.
        </p>
      )}

      {loading && <p className={styles.loading}>Оновлюємо бонусний розрахунок…</p>}
      {error && (
        <div className={styles.error} role="alert">
          <span>{error}</span>
          {onRetry && (
            <button type="button" onClick={onRetry}>
              Спробувати ще раз
            </button>
          )}
        </div>
      )}

      {showLogin && (
        <AuthCustomerModal
          onClose={() => setShowLogin(false)}
          onRegisterClick={openRegister}
        />
      )}
      {showRegister && (
        <RegisterCustomerModal
          onClose={() => setShowRegister(false)}
          onLoginClick={openLogin}
        />
      )}
    </section>
  );
};

export default CheckoutLoyaltyPanel;

